import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Divider } from '@material-ui/core';

import { RiMapPinLine } from 'react-icons/ri';
import { AiOutlineFieldNumber } from 'react-icons/ai';
import { FaSearchLocation, FaSearchPlus, FaStreetView } from 'react-icons/fa';
import { SiOpenstreetmap } from 'react-icons/si';

import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import Input from '../../newComponents/UnformInput';
import * as S from './styles';
import Select from '../../newComponents/UnformSelect';
import GoogleMap from './GoogleMap';

import api from '../../services/api';
import geographicApi from '../../services/geographicApi';

import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import Button from '../../newComponents/Button';
import getValidationErrors from '../../utils/getValidationErrors';

const schema = Yup.object().shape({
  bairro: Yup.string().required('Campo obrigatório'),
  numero: Yup.string().notRequired(),
  cep: Yup.string().required('Campo obrigatório'),
  logradouro: Yup.string().required('Campo obrigatório'),
  complemento: Yup.string().notRequired(),
  cidade: Yup.string().required('Campo obrigatório'),
  estado: Yup.string().required('Campo obrigatório'),
});

interface IOption {
  id: string;
  value: string;
  label: string;
}

interface IAddress {
  bairro: string;
  numero: string;
  cep: string;
  logradouro: string;
  complemento: string;
  cidade: string;
  estado: {
    id: string;
    value: string;
    label: string;
  };
}

const Endereco: React.FC = () => {
  const listEstados: IOption[] = [
    { id: '0', label: 'Acre', value: 'AC' },
    { id: '1', label: 'Alagoas', value: 'AL' },
    { id: '2', label: 'Amapá', value: 'AP' },
    { id: '3', label: 'Amazonas', value: 'AM' },
    { id: '4', label: 'Bahia', value: 'BA' },
    { id: '5', label: 'Ceará', value: 'CE' },
    { id: '6', label: 'Espírito Santo', value: 'ES' },
    { id: '7', label: 'Goiás', value: 'GO' },
    { id: '8', label: 'Maranhão', value: 'MA' },
    { id: '9', label: 'Mato Grosso', value: 'MT' },
    { id: '10', label: 'Mato Grosso do Sul', value: 'MS' },
    { id: '11', label: 'Minas Gerais', value: 'MG' },
    { id: '12', label: 'Pará', value: 'PA' },
    { id: '13', label: 'Paraíba', value: 'PB' },
    { id: '14', label: 'Paraná', value: 'PR' },
    { id: '15', label: 'Pernambuco', value: 'PE' },
    { id: '16', label: 'Piauí', value: 'PI' },
    { id: '17', label: 'Rio de Janeiro', value: 'RJ' },
    { id: '18', label: 'Rio Grande do Norte', value: 'RN' },
    { id: '19', label: 'Rio Grande do Sul', value: 'RS' },
    { id: '20', label: 'Rondônia', value: 'RO' },
    { id: '21', label: 'Roraima', value: 'RR' },
    { id: '22', label: 'Santa Catarina', value: 'SC' },
    { id: '23', label: 'São Paulo', value: 'SP' },
    { id: '24', label: 'Sergipe', value: 'SE' },
    { id: '25', label: 'Tocantins', value: 'TO' },
    { id: '26', label: 'Distrito Federal', value: 'DF' },
  ];
  const initialAddress: IAddress = {
    bairro: '',
    numero: '',
    cep: '',
    logradouro: '',
    complemento: '',
    cidade: '',
    estado: {
      id: '1',
      value: 'AC',
      label: 'Acre',
    },
  };

  const { user } = useAuth();
  const { addToast } = useToast();

  const formRef = useRef<FormHandles>(null);
  const didMountRef = useRef(false);

  const [isLoading, setIsLoading] = useState(true);
  const [address, setAddress] = useState(initialAddress);
  const [latLng, setLatLng] = useState<{
    lat: number | undefined;
    lng: number | undefined;
  } | null>(null);

  const handleAddress = useCallback(
    async (data: IAddress): Promise<void> => {
      formRef.current?.setErrors({});
      setIsLoading(true);

      const {
        numero,
        bairro,
        cidade,
        estado,
        logradouro,
        cep,
        complemento,
      } = data;

      const normalizeCep = cep.replace(/[^0-9]/, '');
      let lat;
      let lon;

      try {
        if (latLng?.lat && latLng.lng) {
          lat = latLng?.lat;
          lon = latLng?.lng;
        } else {
          const response = await geographicApi.getLatLong(
            numero,
            logradouro,
            bairro,
            cidade,
            estado.label,
          );

          lat = response.length > 0 ? response[0].lat : undefined;
          lon = response.length > 0 ? response[0].lon : undefined;
        }

        await schema.validate(
          { ...data, cep: normalizeCep },
          { abortEarly: false },
        );

        await api.put(`/empresa/${user.empresa_id}`, {
          endereco: {
            numero,
            bairro,
            cidade,
            estado,
            logradouro,
            cep: normalizeCep,
            complemento,
          },
          lat,
          lon,
        });

        addToast({
          type: 'success',
          title: 'Dados Alterados!',
        });
        setIsLoading(false);
      } catch (error) {
        console.log(error);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current?.setErrors(errors);
        } else if ((error as any).response?.status === 405) {
          addToast({
            type: 'error',
            title: 'Não Autorizado',
          });
        } else {
          addToast({
            type: 'error',
            title: 'Erro ao editar dados',
            description:
              'Ocorreu um erro ao editar os dados. Verifique os dados no formulário.',
          });
        }
        setIsLoading(false);
      }
    },
    [addToast, latLng, user.empresa_id],
  );

  const useCurrentLocation = (): void => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          setLatLng(pos);
        },
        () => {
          addToast({
            type: 'error',
            title: 'Falha ao buscar localização.',
            description:
              'Ative a localização e dê permissão no navagador para usar',
          });
        },
      );
    } else {
      // Browser doesn't support Geolocation
      addToast({
        type: 'error',
        title: 'Falha ao buscar localização.',
        description:
          'Ative a localização e dê permissão no navagador para usar',
      });
    }
  };

  const searchByCep = async (value: string): Promise<void> => {
    setIsLoading(true);

    try {
      const foundAddress = await geographicApi.getEndereco(value);

      if (foundAddress?.cep) {
        const foundLatAndLog = await geographicApi.getLatLong(
          '',
          foundAddress.logradouro,
          foundAddress.bairro,
          foundAddress.localidade,
          foundAddress.uf,
        );

        const estado =
          listEstados.find((option) => option.value === foundAddress.uf) ||
          initialAddress.estado;

        setAddress((oldState) => ({
          ...oldState,
          estado,
          logradouro: foundAddress.logradouro,
          bairro: foundAddress.bairro,
          cidade: foundAddress.localidade,
          complemento: foundAddress.complemento ? foundAddress.complemento : '',
        }));

        formRef.current?.setFieldValue(`estado`, estado);

        if (foundLatAndLog.length > 0) {
          setLatLng({
            lat: foundLatAndLog[0].lat,
            lng: foundLatAndLog[0].lon,
          });
        }
      }
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const handleCep = (value: string): void => {
    if (value.length < 10) {
      setAddress((oldState) => ({
        ...oldState,
        cep: value.replace(/\D/g, '').replace(/(\d{5})(\d)/, '$1-$2'),
      }));
    }

    if (value.replace(/[^0-9]/, '').length === 8) {
      searchByCep(value.replace(/[^0-9]/, ''));
    }
  };

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
    }

    const loadEndereco = async (): Promise<void> => {
      try {
        const { data } = await api.get(`/empresa/${user.empresa_id}`);

        const { endereco, lat, lon } = data;

        const estado =
          listEstados.find((option) => option.value === endereco.estado) ||
          initialAddress.estado;

        setAddress({
          estado,
          logradouro: endereco.logradouro,
          bairro: endereco.bairro,
          cidade: endereco.cidade,
          numero: endereco.numero,
          cep: endereco.cep.replace(/\D/g, '').replace(/(\d{5})(\d)/, '$1-$2'),
          complemento: endereco.complemento || '',
        });

        formRef.current?.setFieldValue(`estado`, estado);

        if (!lat || !lon) {
          try {
            const res = await geographicApi.getLatLong(
              endereco.numero,
              endereco.logradouro,
              endereco.bairro,
              endereco.cidade,
              endereco.estado,
            );

            setLatLng({
              lat: res.length > 0 ? res[0].lat : undefined,
              lng: res.length > 0 ? res[0].lon : undefined,
            });
          } catch (error) {
            console.log(error);
            setLatLng({ lat, lng: lon });
          }
        } else {
          setLatLng({ lat, lng: lon });
        }
        setIsLoading(false);
      } catch (error) {
        console.log(error);
        addToast({
          type: 'error',
          title: 'Erro na busca do endereço.',
        });
        setIsLoading(false);
      }
    };
    loadEndereco();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.empresa_id]);

  return (
    <S.Container>
      <div className="header">
        <h2>Edite seu endereço</h2>
      </div>

      <Divider />

      <S.Conteudo>
        {latLng?.lat && latLng?.lng && (
          <GoogleMap
            useCurrentLocation={useCurrentLocation}
            lat={latLng.lat}
            lon={latLng.lng}
            setLatLng={setLatLng}
          />
        )}

        <Form ref={formRef} onSubmit={handleAddress} initialData={address}>
          <div className="inputs one">
            <Input
              name="bairro"
              icon={RiMapPinLine}
              label="Bairro:"
              placeholder="Ex.: Copacabana"
              disabled={isLoading}
            />
            <Input
              name="numero"
              icon={AiOutlineFieldNumber}
              label="Número:"
              placeholder="Ex.: 123"
              disabled={isLoading}
            />
            <Input
              name="cep"
              icon={FaSearchLocation}
              label="Cep:"
              placeholder="Ex.: 12345-67"
              disabled={isLoading}
              value={address.cep}
              onChange={(e) => handleCep(e.target.value)}
            />
          </div>
          <div className="inputs two">
            <Input
              name="logradouro"
              icon={FaStreetView}
              label="Logradouro:"
              placeholder="Ex.: Rua Dois"
              disabled={isLoading}
            />
            <Input
              name="complemento"
              icon={FaSearchPlus}
              label="Complemento:"
              disabled={isLoading}
            />
          </div>
          <div className="inputs three">
            <Input
              name="cidade"
              icon={SiOpenstreetmap}
              label="Cidade:"
              disabled={isLoading}
            />
            <div className="select_states">
              <label htmlFor="estado">Estado:</label>
              <Select
                name="estado"
                placeholder="Ex.: Brasilia"
                defaultValue={address.estado}
                options={listEstados}
                disabled={isLoading}
              />
            </div>
          </div>
          <Button full type="submit" disabled={isLoading}>
            Salvar
          </Button>
        </Form>
      </S.Conteudo>
    </S.Container>
  );
};

export default Endereco;
